import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { getBrowserTimezone } from './TimezoneAutocomplete';
import type { DateTime } from './utils/dates';

const UserTimezoneContext = createContext<string | null>(null);

interface UserTimezoneProviderProps {
  timezone: string;
  children: ReactNode;
}

export function UserTimezoneProvider({
  timezone,
  children,
}: UserTimezoneProviderProps) {
  return (
    <UserTimezoneContext.Provider value={timezone}>
      {children}
    </UserTimezoneContext.Provider>
  );
}

export function useUserTimezone(): string {
  return useContext(UserTimezoneContext) ?? getBrowserTimezone();
}

interface TimezoneDisplay<T extends boolean> {
  displayTime: DateTime<T>;
  locationTime: DateTime<T> | null;
  userTimezone: string;
  locationTimezone: string | undefined;
}

export function useTimezoneDisplay<T extends boolean>(
  dateTime: DateTime<T>,
  locationTimezone?: string
): TimezoneDisplay<T> {
  const userTimezone = useUserTimezone();

  return useMemo(() => {
    const displayTime = dateTime.setZone(userTimezone) as DateTime<T>;
    const locationTime = locationTimezone
      ? (dateTime.setZone(locationTimezone) as DateTime<T>)
      : null;

    return {
      displayTime,
      locationTime,
      userTimezone,
      locationTimezone,
    };
  }, [dateTime, userTimezone, locationTimezone]);
}
